import React, { Fragment, useCallback, useRef } from 'react';
import type { AnnotatedSuperNodeT, ProtectedFields } from '../types';
import { remapNodesHighlighted } from '../utils/annotations';
import { superNodeNeedsSpaceBefore } from '../utils/spacing';
import HighlightedSuperNode from './HighlightedSuperNode';

type HighlightedSentenceProps = {
  nodes: AnnotatedSuperNodeT[];
  useHover: boolean;
  protectedFields?: ProtectedFields;
  portal?: string;
  hideHighlight?: boolean;
  ruleId?: string;
  blockHighlight?: boolean;
  borderSeparation?: string;
  modelTransparencyMode?: boolean;
  highlightSentenceOnClickHandle?: (node: number) => void;
  translationLanguage?: string;

  setHoverData?: (hoverData: {
    line: number;
    hoverIndex: number;
    firstNode: number;
    lastNode: number;
    yPos: number;
  }) => void;
};

const HighlightedSentence: React.FC<HighlightedSentenceProps> = (props) => {
  const {
    nodes,
    useHover,
    protectedFields,
    portal,
    hideHighlight = false,
    ruleId,
    blockHighlight = false,
    modelTransparencyMode = false,
    borderSeparation = 'pl-1',
    highlightSentenceOnClickHandle,
    translationLanguage = '',
    setHoverData,
  } = props;

  const memoizedNodesContainerRef = useRef<HTMLDivElement>(null);

  const memoizedNodes = React.useMemo<AnnotatedSuperNodeT[]>(
    () => remapNodesHighlighted(nodes),
    [nodes]
  );

  const getSentenceGroup = useCallback(
    (index: number): [number, number] => {
      const hoverNode = index;
      let firstNode = -1;
      let lastNode = -1;

      const highlightGroups = memoizedNodes.reduce<AnnotatedSuperNodeT[][]>(
        (acc, cur) => {
          const curIndex = cur.nodes[0].index;
          if (curIndex === 0) {
            return [...acc, [cur]];
          }
          acc[acc.length - 1].push(cur);
          return acc;
        },
        []
      );

      const foundGroup = highlightGroups.find((g) =>
        g.some((n) => n.index === hoverNode)
      );

      if (foundGroup) {
        firstNode = foundGroup[0].index;
        lastNode = foundGroup[foundGroup.length - 1].index;
      }
      return [firstNode, lastNode];
    },
    [memoizedNodes]
  );

  const containerClass = (): string => {
    const className = `${borderSeparation} pr-5`;
    return className;
  };

  const renderSpace = (
    superNode: AnnotatedSuperNodeT,
    idx: number
  ): JSX.Element => {
    if (
      blockHighlight &&
      superNode.sentenceHighlight &&
      superNodeNeedsSpaceBefore(nodes, idx)
    ) {
      let background = '';
      if (superNode.sentenceHighlight === 'highlight') {
        background = 'bg-litlingo-highlight';
      }
      if (superNode.sentenceHighlight === 'underline') {
        background = 'bg-litlingo-gray-1';
      }
      if (
        superNode.sentenceHighlight &&
        superNode.sentenceHighlight !== 'highlight' &&
        superNode.sentenceHighlight !== 'underline'
      ) {
        background = superNode.sentenceHighlight;
      }

      if (modelTransparencyMode) {
        if (superNode.annotations?.some((h) => h.highlight)) {
          background = 'bg-litlingo-highlight';
        } else {
          background = 'bg-litlingo-gray-1';
        }
      }

      return <span className={`inline-block ${background}`}>{'\u00A0'}</span>;
    }
    if (superNodeNeedsSpaceBefore(nodes, idx)) return <> </>;

    return <></>;
  };

  const getSentenceHighlight = (superNode: AnnotatedSuperNodeT): string => {
    if (modelTransparencyMode) {
      if (superNode.annotations?.some((h) => h.highlight)) {
        return 'highlight';
      }
      if (superNode.sentenceHighlight) {
        return 'underline';
      }
      return '';
    }
    if (blockHighlight) return superNode.sentenceHighlight || '';

    return '';
  };

  const handleHover = (i: number): void => {
    let yPos = -1;
    const [firstNode, lastNode] = getSentenceGroup(i);
    if (memoizedNodesContainerRef.current) {
      const node = memoizedNodesContainerRef.current.querySelector(
        `#supernode-${firstNode}`
      );
      if (node) {
        yPos =
          node.getBoundingClientRect().y -
          memoizedNodesContainerRef.current.getBoundingClientRect().y;
      }
    }
    if (setHoverData) {
      setHoverData({ line: -1, hoverIndex: i, firstNode, lastNode, yPos });
    }
  };

  return (
    <div
      ref={memoizedNodesContainerRef}
      className={`relative ${containerClass()}`}
    >
      {memoizedNodes.map((superNode, idx) => (
        <Fragment key={superNode.index}>
          <span
            id={`supernode-${superNode.index}`}
            onMouseEnter={(): void => {
              if (superNode.sentenceHighlight !== undefined) {
                handleHover(superNode.index);
              }
            }}
          >
            {renderSpace(superNode, idx)}
            <HighlightedSuperNode
              superNode={superNode}
              nodes={superNode.nodes}
              annotations={superNode.annotations}
              useHover={useHover}
              protectedFields={protectedFields}
              portal={portal}
              hideHighlight={hideHighlight || blockHighlight}
              ruleId={ruleId}
              sentenceHighlight={getSentenceHighlight(superNode)}
              highlightSentenceOnClickHandle={highlightSentenceOnClickHandle}
            />
          </span>
        </Fragment>
      ))}
      {translationLanguage && (
        <span className="italic">{` (${translationLanguage} to English translation)`}</span>
      )}
    </div>
  );
};

HighlightedSentence.defaultProps = {
  protectedFields: { entity: 'FIELD_IS_HIDDEN' },
};

export default HighlightedSentence;
